import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import './Header.css';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMegaMenuVisible, setIsMegaMenuVisible] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const location = useLocation();

  // Close mega menu on route change
  useEffect(() => {
    setIsMegaMenuVisible(false);
  }, [location]);

  // Close mobile menu on route change
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
      setActiveDropdown(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Handle body scroll lock when sidebar is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    if (windowWidth > 1024 && mobileMenuOpen) {
      setMobileMenuOpen(false);
      setActiveDropdown(null);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [windowWidth, mobileMenuOpen]);

  const toggleMobileMenu = () => {
    if (mobileMenuOpen) {
      setActiveDropdown(null);
    }
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleSubmenu = (key) => {
    if (activeDropdown === key) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(key);
    }
  };

  const handleMegaItemClick = () => {
    setIsMegaMenuVisible(false);
    closeMobileMenu();
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsMegaMenuVisible(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsMegaMenuVisible(false);
    }, 150);
  };

  // Data for mega menu and mobile sidebar
  const leftColumnSections = [
    {
      category: "TECHNOLOGY & CONSULTANCY",
      items: [
        { name: "AALITECH", desc: "Technology & digital solutions", path: "/companies/aalitech", icon: "/assets/opt/company-logo.webp" },
        { name: "AALI CONSSULTANCY", desc: "Jobs & education consultancy", path: "/companies/aaliconssultancy", icon: "/assets/opt/icon-consultancy.webp" }
      ]
    },
    {
      category: "INFRASTRUCTURE & DEVELOPMENT",
      items: [
        { name: "AALI BUILDERS", desc: "Infrastructure development", path: "/companies/aalibuilders", icon: "/assets/opt/company-logo.webp" }
      ]
    }
  ];

  const centerColumn = {
    category: "ENTERTAINMENT",
    items: [
      { name: "AALI MOVIES", desc: "Film production house", path: "/companies/aalimovies", icon: "/assets/opt/icon-movies.webp" },
      { name: "AALI FILMZ", desc: "Digital content studio", path: "/companies/aalifilmz", icon: "/assets/opt/icon-filmz.webp" },
      { name: "AALI MUSIC", desc: "Music label & production", path: "/companies/aalimusic", icon: "/assets/opt/icon-music.webp" }
    ]
  };

  const rightColumnSections = [
    {
      category: "LUXURY & RETAIL",
      items: [
        { name: "AALI GOLD JEWELZ", desc: "Luxury jewellery brand", path: "/companies/aaligoldjewelz", icon: "/assets/opt/icon-gold.webp" }
      ]
    },
    {
      category: "SOCIAL IMPACT",
      items: [
        { name: "AALI TRUSTZ", desc: "Charitable & social initiatives", path: "/companies/aalitrustz", icon: "/assets/opt/icon-trustz.webp" }
      ]
    }
  ];

  const allMobileCategories = [
    leftColumnSections[0],
    leftColumnSections[1],
    centerColumn,
    ...rightColumnSections
  ];

  // Helper to get icon based on category
  const getCategoryIcon = (category) => {
    if (category.includes("TECHNOLOGY")) return "fas fa-microchip";
    if (category.includes("INFRASTRUCTURE")) return "fas fa-hard-hat";
    if (category.includes("ENTERTAINMENT")) return "fas fa-film";
    if (category.includes("LUXURY")) return "fas fa-gem";
    if (category.includes("SOCIAL")) return "fas fa-hand-holding-heart";
    return "fas fa-building";
  };

  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to="/" className="logo" onClick={closeMobileMenu}>
          <img src="/assets/opt/company-logo.webp" alt="AALI Group Logo" />
          <span className="logo-text">AALI GROUP</span>
        </Link>
        
        <div
          className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleMobileMenu(); } }}
          role="button"
          tabIndex={0}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </div>
        
        <ul className="nav-menu">
          <li><NavLink to="/" className="nav-link" end>Home</NavLink></li>
          <li><NavLink to="/about" className="nav-link">About</NavLink></li>
          <li 
            className="mega-dropdown"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button className="nav-link dropdown-btn" aria-haspopup="true" aria-expanded={isMegaMenuVisible}>
              Our Businesses <i className="fas fa-chevron-down dropdown-icon" aria-hidden="true"></i>
            </button>
            <div className={`mega-menu ${isMegaMenuVisible ? 'visible' : ''}`}>
              <div className="mega-columns">
                <div className="mega-section">
                  {leftColumnSections.map((section, idx) => (
                    <div key={idx} className="mega-subsection">
                      <h4>{section.category}</h4>
                      {section.items.map(item => (
                        <Link to={item.path} className="mega-item" key={item.name} onClick={handleMegaItemClick}>
                          <img src={item.icon} alt={item.name} />
                          <div><strong>{item.name}</strong><p>{item.desc}</p></div>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="mega-section">
                  <h4>{centerColumn.category}</h4>
                  {centerColumn.items.map(item => (
                    <Link to={item.path} className="mega-item" key={item.name} onClick={handleMegaItemClick}>
                      <img src={item.icon} alt={item.name} />
                      <div><strong>{item.name}</strong><p>{item.desc}</p></div>
                    </Link>
                  ))}
                </div>
                <div className="mega-section">
                  {rightColumnSections.map((section, idx) => (
                    <div key={idx} className="mega-subsection">
                      <h4>{section.category}</h4>
                      {section.items.map(item => (
                        <Link to={item.path} className="mega-item" key={item.name} onClick={handleMegaItemClick}>
                          <img src={item.icon} alt={item.name} />
                          <div><strong>{item.name}</strong><p>{item.desc}</p></div>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="mega-footer">
                <Link to="/businesses" className="mega-view-all" onClick={handleMegaItemClick}>
                  View all businesses →
                </Link>
              </div>
            </div>
          </li>
          <li><NavLink to="/investors" className="nav-link">Investors</NavLink></li>
          <li><NavLink to="/newsroom" className="nav-link">Newsroom</NavLink></li>
          <li><NavLink to="/careers" className="nav-link">Careers</NavLink></li>
          <li><NavLink to="/contact" className="nav-link">Contact</NavLink></li>
        </ul>
      </div>

      {/* Overlay */}
      <div className={`sidebar-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu}></div>

      {/* Professional Mobile Sidebar */}
      <div className={`mobile-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/assets/opt/company-logo.webp" alt="AALI Group Logo" />
            <span>AALI GROUP</span>
          </div>
          <button className="close-sidebar" onClick={closeMobileMenu} aria-label="Close menu">
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <div className="sidebar-nav">
          <ul className="sidebar-nav-list">
            <li className="sidebar-nav-item">
              <Link to="/" className="sidebar-link" onClick={closeMobileMenu}>
                <span><i className="fas fa-home"></i> Home</span>
              </Link>
            </li>
            <li className="sidebar-nav-item">
              <Link to="/about" className="sidebar-link" onClick={closeMobileMenu}>
                <span><i className="fas fa-info-circle"></i> About</span>
              </Link>
            </li>
            <li className="sidebar-nav-item">
              <div className={`sidebar-link submenu-trigger`} onClick={() => toggleSubmenu('businesses')}>
                <span><i className="fas fa-briefcase"></i> Our Businesses</span>
                <i className={`fas fa-chevron-down chevron-icon ${activeDropdown === 'businesses' ? 'rotated' : ''}`}></i>
              </div>
              <ul className={`sidebar-submenu ${activeDropdown === 'businesses' ? 'open' : ''}`}>
                {allMobileCategories.map((section, idx) => (
                  <React.Fragment key={section.category}>
                    <li className="submenu-category">
                      <i className={getCategoryIcon(section.category)} style={{ marginRight: '6px', fontSize: '10px' }}></i> {section.category}
                    </li>
                    {section.items.map(item => (
                      <li key={item.name}>
                        <Link to={item.path} className="sidebar-submenu-item" onClick={closeMobileMenu}>
                          <i className="fas fa-building"></i> {item.name}
                        </Link>
                      </li>
                    ))}
                  </React.Fragment>
                ))}
                <li>
                  <Link to="/businesses" className="sidebar-submenu-item view-all-link" onClick={closeMobileMenu}>
                    <i className="fas fa-arrow-right"></i> View all businesses
                  </Link>
                </li>
              </ul>
            </li>
            <li className="sidebar-nav-item">
              <Link to="/investors" className="sidebar-link" onClick={closeMobileMenu}>
                <span><i className="fas fa-chart-line"></i> Investors</span>
              </Link>
            </li>
            <li className="sidebar-nav-item">
              <Link to="/newsroom" className="sidebar-link" onClick={closeMobileMenu}>
                <span><i className="fas fa-newspaper"></i> Newsroom</span>
              </Link>
            </li>
            <li className="sidebar-nav-item">
              <Link to="/careers" className="sidebar-link" onClick={closeMobileMenu}>
                <span><i className="fas fa-rocket"></i> Careers</span>
              </Link>
            </li>
            <li className="sidebar-nav-item">
              <Link to="/contact" className="sidebar-link" onClick={closeMobileMenu}>
                <span><i className="fas fa-envelope"></i> Contact</span>
              </Link>
            </li>
          </ul>
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-social">
            {/* Instagram/Facebook open AALI CONSSULTANCY for now; YouTube and Twitter have no profile yet (no link) */}
            <a href="https://www.instagram.com/aaliconssultancy/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="AALI CONSSULTANCY on Instagram"><i className="fab fa-instagram"></i></a>
            <a href="https://www.facebook.com/profile.php?id=61585917585366" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="AALI CONSSULTANCY on Facebook"><i className="fab fa-facebook-f"></i></a>
            <a className="social-icon" aria-hidden="true" style={{ cursor: 'default' }}><i className="fab fa-youtube"></i></a>
            <a className="social-icon" aria-hidden="true" style={{ cursor: 'default' }}><i className="fab fa-twitter"></i></a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;